import { createPortal } from 'react-dom';

import { CARD_WIDTH } from 'constants/widths';
// Imported directly rather than through the `hooks` barrel, which pulls in
// `components/shared` (and its import cycle) behind a one-hook dependency.
import { useScrolling } from 'hooks/useScrolling';

import { ActivityCardPragmatic } from '../ActivityCardPragmatic/ActivityCardPragmatic';
import MultiSelectDragPreview from '../MultiSelectDragPreview/MultiSelectDragPreview';

import { FrozenCard } from './FrozenDropCardContent.styles';

import type { ReactPortal } from 'react';
import type { FrozenDropCardContentProps } from './FrozenDropCardContent.types';

/**
 * The frozen card itself, portaled to `document.body` so it sits above the
 * scroll containers while keeping the React context (Recoil / Redux / MUI /
 * Router) that `ActivityCardPragmatic` needs.
 *
 * Anchored at a fixed viewport position captured at drop time, so it would drift
 * out of sync if the (often long) card list is scrolled while the call is still
 * loading. To avoid showing it in the wrong place, it hides itself as soon as the
 * user scrolls (the source card stays faded and the column overlay +
 * sticky-header spinner keep indicating the load).
 */
const FrozenDropCardContent = ({
  frozenDrop
}: FrozenDropCardContentProps): ReactPortal | null => {
  'use memo';

  const {
    position,
    width,
    count,
    event,
    planningEntityId,
    planningEntityType
  } = frozenDrop;

  const hasScrolled = useScrolling();
  if (hasScrolled) return null;

  return createPortal(
    <FrozenCard
      style={{
        top: position.y,
        left: position.x,
        width: `${width || CARD_WIDTH}px`
      }}
    >
      {count > 1 ? (
        <MultiSelectDragPreview count={count} />
      ) : (
        <ActivityCardPragmatic
          {...event}
          planningEntityId={planningEntityId}
          planningEntityType={planningEntityType}
          selected={false}
          hovered={false}
        />
      )}
    </FrozenCard>,
    document.body
  );
};

export default FrozenDropCardContent;
